/*import {
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/outline'*/
import clsx from 'clsx'
import IntLink from 'components/common/IntLink'
import LoadingOverlay from 'components/common/LoadingOverlay'
//import Button from 'components/forms/Button'
import 'dayjs/locale/ru'
import useSlug from 'hooks/useSlug'
import { getServiceDetails, getServices } from 'lib/getServices'
import {
  isAbsent,
  isDiff,
  isEmptyObj,
  isNonEmptyArray,
  isNonEmptyObj,
  isNonEmptyString,
  isPresent,
  isSame,
  toPairs,
  slashSiteLinks,
} from 'lib/utils'
import Head from 'next/head'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  IGetServiceDetailsResult,
  selectSpecials,
  useLazyGetSpecialsQuery,
  type IGetSpecialsResult,
  type IServicePortfolio,
} from 'store/api'
import { selectIsBrowser, selectIsLg, selectIsSm } from 'store/ui'
import PortfolioSlider from './PortfolioSlider'
//import ServiceSuggestPopup from './ServiceSuggestPopup'
import ServiceForm from './ServiceForm'
import SuggestedProducts from './SuggestedProducts'

type Props = {
  serviceDetails: IGetServiceDetailsResult
}

const ServiceView = ({
  serviceDetails: serviceDetailsSSR,
}: Props) => {
  const { slug, queryLng } = useSlug()

  const isSm = useSelector(selectIsSm)
  const isLg = useSelector(selectIsLg)

  let isBrowser = useSelector(selectIsBrowser)
  let specials = useSelector(selectSpecials(queryLng))
  const [selectedSub, setSelectedSub] =
    useState<IGetSpecialsResult['sub'][number]>()
  let [getSpecials] = useLazyGetSpecialsQuery()

  const [details, setDetails] =
    useState<IGetServiceDetailsResult | null>()
  const [groupedPortfolios, setGroupedPortfolios] = useState<
    {
      label: string
      items: IServicePortfolio[]
    }[]
  >([])

  const [loading, setLoading] = useState(false)
  //const [showMore, setShowMore] = useState(false)
  //const [showMoreSub, setShowMoreSub] = useState(false)

  useEffect(() => {
    const f = async () => {
      setLoading(true)
      const [detailsData, servicesData] = await Promise.all([
        getServiceDetails('ru', slug as string),
        getServices('ru'),
      ])
      setLoading(false)

      if (isPresent(detailsData)) setDetails(detailsData)

      if (isPresent(servicesData))
        setGroupedPortfolios(
          toPairs(
            servicesData.portfolio.reduce((acc, cur) => {
              acc[cur.type_label] = (
                acc[cur.type_label] ?? []
              ).concat(cur)
              return acc
            }, {} as Record<IServicePortfolio['type_label'], IServicePortfolio[]>)
          ).map(([label, items]) => ({ label, items }))
        )
    }
    void f()
  }, [slug])

  useEffect(() => {
    if (isBrowser) {
      void getSpecials(queryLng)
    }
  }, [getSpecials, isBrowser, queryLng])

  useEffect(() => {
    isPresent(specials?.sub[0]?.id) &&
      setSelectedSub(specials!.sub[0])
  }, [specials])

  const service = useMemo(
    () =>
      isNonEmptyObj(serviceDetailsSSR) &&
      isSame(serviceDetailsSSR.service.slug, slug)
        ? serviceDetailsSSR.service
        : details?.service,
    [serviceDetailsSSR, slug, details]
  )

  if (loading)
    return (
      <main className='px-4 md:px-6 lg:px-8 xl:px-16 bg-white flex-grow py-64 relative'>
        <LoadingOverlay className='h-5 w-5 mx-2.5 bg-accent' open />
      </main>
    )

  if (isAbsent(service) || isEmptyObj(service))
    return (
      <div className='flex flex-col px-4'>
        <h1 className='mt-20 text-2xl xl:text-4xl text-center leading-snug'>
          Ошибка 404 <br /> Нет такой страницы
        </h1>
      </div>
    )

  return (
    <>
      <Head>
        <title>{service.meta.title}</title>
        <meta name='apple-mobile-web-app-title' content={service.meta.title} />
        <meta property='og:site_name' content={service.meta.title} />
        <meta name='description' content={service.meta.description} />
        <meta name='keywords' content={service.meta.keywords} />
      </Head>

      {isLg && (
        <div className='mt-4 py-4 pr-0 custom-px'>
          <div className='w-full'>
            <div className='hidden lg:block text-sm2 leading-tight tracking-tight'>
              <IntLink
                className='text-dark underline hover:text-accent'
                href='/'
              >
                Главная
              </IntLink>

              {' — '}
              <IntLink
                className='text-dark underline hover:text-accent'
                href='/catering'
              >
                Кейтеринг
              </IntLink>

              <span className='text-greyA1'>{` — ${service.meta.title}`}</span>
            </div>
          </div>
        </div>
      )}

      <div className='mb-12 custom-px'>
        <h1 className='pt-4 lg:pt-0 lg:mt-6 font-unbounded font-medium text-2xl lg:text-5xl lg:leading-tight tracking-tight'>
          {service.name}
        </h1>

        <div className='mt-4 pb-8 lg:border-b border-lightGrey flex flex-col lg:flex-row gap-x-14 gap-y-8'>
          <div
            className='text-base2 leading-snug text-bodyGrey'
            dangerouslySetInnerHTML={{ __html: slashSiteLinks(service.text) }}
          ></div>

          {/*<div>
            <div
              className={clsx(
                'text-base2 leading-snug text-bodyGrey',
                {
                  'max-h-[87px] max-h-[178px] bg-clip-text overflow-hidden':
                    !showMore,
                }
              )}
              dangerouslySetInnerHTML={{ __html: slashSiteLinks(service.text) }}
            />

            <div className='flex flex-row items-center'>
              <Button
                className='pl-0 pr-1 text-gray-600 text-sm lg:text-base'
                onClick={() => setShowMore((prev) => !prev)}
              >
                {showMore ? 'Свернуть' : 'Читать дальше'}
              </Button>

              {showMore ? (
                <ChevronUpIcon className='w-3.5 h-3.5' />
              ) : (
                <ChevronDownIcon className='w-3.5 h-3.5' />
              )}
            </div>
          </div>*/}

          {/* <div className='w-full lg:w-1/2'>
            <div className='aspect-w-3 aspect-h-2'>
              <Image
                src={`${MEDIA_URL}${service.image}`}
                placeholder='blur'
                blurDataURL={BLUR_DATA_URL}
                alt=''
                layout='fill'
                objectFit='cover'
              />
            </div>
          </div> */}
        </div>

        {isNonEmptyArray(specials?.sub) &&
          isNonEmptyArray(selectedSub?.products) && (
            <>
              <div>
                <h2 className='pt-4 lg:pt-0 lg:mt-6 font-unbounded font-medium text-2xl lg:text-5xl lg:leading-tight tracking-tight'>
                  {specials!.name}
                </h2>
                {/*{!isSm && (
                  <h2 className='pt-4 lg:pt-0 lg:mt-6 font-unbounded font-medium text-2xl lg:text-5xl lg:leading-tight tracking-tight'>
                    {specials!.name}
                  </h2>
                )}*/}

                <div className='flex mt-5 lg:mt-10 gap-x-3 overflow-x-auto lg:overflow-x-visible omit-scrollbar thin-scrollbar'>
                  {specials!.sub.map((sub) => (
                    <div
                      key={sub.id}
                      onClick={() => setSelectedSub(sub)}
                      className={clsx(
                        'text-xs lg:text-base2 flex-grow font-semibold tracking-tight py-2.5 lg:py-3 px-3 lg:px-6 text-center relative whitespace-nowrap rounded-lg',
                        {
                          'bg-primary cursor-default': isSame(
                            sub.id,
                            selectedSub?.id
                          ),
                          'bg-gray-100 cursor-pointer hover:bg-lightYellow':
                            isDiff(sub.id, selectedSub?.id),
                        }
                      )}
                    >
                      {sub.name}
                    </div>
                  ))}
                </div>

                {isNonEmptyString(selectedSub?.text) && (
                  <div className='lg:mt-6 lg:px-10 py-6 text-sm lg:text-base2 leading-snug lg:bg-gray-100 relative rounded-lg'>
                    <div
                      className='text-gray-600'
                      dangerouslySetInnerHTML={{
                        __html: slashSiteLinks(selectedSub!.text),
                      }}
                    ></div>
                    {/*<div
                      className={clsx('text-gray-600', {
                        'max-h-[24px] bg-clip-text overflow-hidden':
                          isSm && !showMoreSub,
                      })}
                      dangerouslySetInnerHTML={{
                        __html: slashSiteLinks(selectedSub!.text),
                      }}
                    ></div>

                    {isSm && (
                      <div className='flex flex-row items-center'>
                        <Button
                          className='pl-0 pr-1 text-gray-600 text-sm'
                          onClick={() =>
                            setShowMoreSub((prev) => !prev)
                          }
                        >
                          {showMoreSub ? 'Свернуть' : 'Читать дальше'}
                        </Button>

                        {showMoreSub ? (
                          <ChevronUpIcon className='w-3.5 h-3.5' />
                        ) : (
                          <ChevronDownIcon className='w-3.5 h-3.5' />
                        )}
                      </div>
                    )}*/}
                  </div>
                )}
              </div>

              <div
                className={clsx(
                  'lg:mt-6 lg:px-10 lg:py-6 lg:bg-gray-100 relative rounded-xl',
                  {
                    'mt-6':
                      isSm && !isNonEmptyString(selectedSub?.text),
                  }
                )}
              >
                <SuggestedProducts products={selectedSub!.products} />

                <div className='flex flex-row justify-center'>
                  <IntLink
                    className='mt-4 btn-primary py-3 px-4 w-full lg:w-1/2 text-center text-xs md:text-sm font-semibold rounded-lg lg:rounded'
                    href='/'
                  >
                    Перейти в меню
                  </IntLink>
                </div>
              </div>
            </>
          )}
      </div>

      {isNonEmptyArray(groupedPortfolios) && (
        <div
          className={`custom-px ${
            isNonEmptyArray(specials?.sub) &&
            isNonEmptyArray(selectedSub?.products)
              ? 'mt-20'
              : '-mt-12'
          }`}
        >
          {groupedPortfolios?.map((section) => (
            <PortfolioSlider key={section.label} section={section} />
          ))}
        </div>
      )}

      {/*<div className='sticky bottom-0 mt-12 left-0 px-0 lg:pr-0 lg:pr-12 xl:pr-24 2xl:pr-48 lg:pl-12 xl:pl-24 2xl:pl-48 md:mb-12 z-[11]'>
        <ServiceSuggestPopup />
      </div>*/}
      <div className='custom-px py-10'>
        <div className='inline-block align-bottom bg-lightGrey px-4 md:px-6 lg:px-8 xl:px-9 text-left transform transition-all sm:align-middle sm:px-6 py-9 w-full rounded-[12px]'>
            <ServiceForm />
          </div>
      </div>
    </>
  )
}

export default ServiceView
